import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Star } from 'lucide-react';
export function HeroSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  return <section ref={ref} className="relative min-h-screen flex flex-col items-center justify-center px-4 py-20 overflow-hidden" style={{
    backgroundColor: '#050A14'
  }} data-id="element-78">
      {/* Background radial glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 90% 55% at 50% -5%, rgba(14,165,233,0.13) 0%, transparent 65%), radial-gradient(ellipse 50% 40% at 85% 85%, rgba(129,140,248,0.07) 0%, transparent 60%)'
    }} data-id="element-79" />

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(rgba(14,165,233,1) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,1) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-80" />

      {/* Floating nodes */}
      {[{
      x: '8%',
      y: '20%',
      size: 5,
      color: '#0EA5E9',
      delay: 0
    }, {
      x: '92%',
      y: '15%',
      size: 4,
      color: '#818CF8',
      delay: 0.8
    }, {
      x: '15%',
      y: '75%',
      size: 3,
      color: '#38BDF8',
      delay: 1.4
    }, {
      x: '88%',
      y: '70%',
      size: 5,
      color: '#0EA5E9',
      delay: 0.4
    }, {
      x: '50%',
      y: '88%',
      size: 3,
      color: '#818CF8',
      delay: 1.1
    }, {
      x: '30%',
      y: '10%',
      size: 4,
      color: '#38BDF8',
      delay: 0.6
    }].map((node, i) => <motion.div key={i} className="absolute rounded-full" style={{
      width: node.size,
      height: node.size,
      backgroundColor: node.color,
      left: node.x,
      top: node.y,
      opacity: 0.5,
      boxShadow: `0 0 ${node.size * 3}px ${node.color}`
    }} animate={{
      y: [0, -10, 0],
      opacity: [0.5, 0.8, 0.5]
    }} transition={{
      duration: 3.5 + i * 0.4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: node.delay
    }} data-id="element-81" />)}

      <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-82">
        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="inline-flex items-center gap-2 mb-7 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase" style={{
        border: '1px solid rgba(14,165,233,0.3)',
        color: '#38BDF8',
        backgroundColor: 'rgba(14,165,233,0.07)',
        backdropFilter: 'blur(8px)'
      }} data-id="element-83">
          <Star size={11} fill="#38BDF8" data-id="element-84" />
          PARA PADRES HISPANOS EN EE.UU.
          <Star size={11} fill="#38BDF8" data-id="element-85" />
        </motion.div>

        {/* Main Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.75,
        delay: 0.1
      }} className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-7 tracking-tight" style={{
        background: 'linear-gradient(135deg, #F1F5F9 15%, #38BDF8 55%, #818CF8 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-86">
          ¿Estás criando a un futuro empleado que la IA reemplazará, o al líder
          que dará las órdenes?
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.65,
        delay: 0.2
      }} className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed" style={{
        color: '#94A3B8'
      }} data-id="element-87">
          El Título Universitario ya no es garantía. Dale a tu hijo la{' '}
          <span style={{
          color: '#F59E0B',
          fontWeight: 700
        }} data-id="element-88">
            "Ventaja Competitiva"
          </span>{' '}
          en la Era de la IA.
        </motion.p>

        {/* Product Mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.93,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        scale: 1,
        y: 0
      } : {}} transition={{
        duration: 0.85,
        delay: 0.3
      }} className="mb-10 relative inline-block" data-id="element-89">
          <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(14,165,233,0.18) 0%, transparent 70%)',
          filter: 'blur(20px)',
          transform: 'scale(1.1)'
        }} data-id="element-90" />
          <img src="https://cdn.magicpatterns.com/uploads/vTrnTNJCdGgAL2C5jFH1Af/Mockup_-_HeyLloyd_6.png" alt="Arquitectos del Futuro — Manual Digital" className="relative mx-auto max-w-sm md:max-w-md lg:max-w-lg w-full" style={{
          filter: 'drop-shadow(0 0 50px rgba(14,165,233,0.25)) drop-shadow(0 30px 70px rgba(0,0,0,0.7))'
        }} data-id="element-91" />
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.45
      }} className="flex flex-col items-center gap-4" data-id="element-92">
          <motion.button onClick={() => {
          const card = document.getElementById('pricing-card');
          if (card) {
            card.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }} whileHover={{
          scale: 1.04
        }} whileTap={{
          scale: 0.97
        }} animate={{
          boxShadow: ['0 0 20px rgba(245,159,11,0.35)', '0 0 45px rgba(245,159,11,0.65)', '0 0 20px rgba(245,159,11,0.35)']
        }} transition={{
          boxShadow: {
            duration: 2.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} className="px-10 py-5 rounded-full text-lg font-black uppercase tracking-wide cursor-pointer" style={{
          backgroundColor: '#F59E0B',
          color: '#050A14'
        }} data-id="element-93">
            QUIERO EL MANUAL ESTRATÉGICO →
          </motion.button>

          <div className="flex flex-wrap items-center justify-center gap-3 text-sm" style={{
          color: '#475569'
        }} data-id="element-94">
            <Shield size={13} style={{
            color: '#0EA5E9'
          }} data-id="element-95" />
            <span data-id="element-96">Acceso inmediato</span>
            <span style={{
            color: '#1E3A5F'
          }} data-id="element-97">
              ·
            </span>
            <span data-id="element-98">Formato digital</span>
            <span style={{
            color: '#1E3A5F'
          }} data-id="element-99">
              ·
            </span>
            <span data-id="element-100">Garantía de satisfacción</span>
          </div>
        </motion.div>

        {/* Social proof */}
        <motion.div initial={{
        opacity: 0
      }} animate={inView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="mt-10 flex items-center justify-center gap-3" data-id="element-101">
          <div className="flex -space-x-2" data-id="element-102">
            {[0, 1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold" style={{
            backgroundColor: ['#0C2A4A', '#0F1E38', '#0C1628', '#0A1830'][i],
            borderColor: '#0EA5E9',
            color: '#38BDF8'
          }} data-id="element-103">
                {['M', 'A', 'L', 'C'][i]}
              </div>)}
          </div>
          <span className="text-sm" style={{
          color: '#64748B'
        }} data-id="element-104">
            +1,200 padres ya tomaron el control
          </span>
        </motion.div>
      </div>
    </section>;
}